<template>
  <div>
    <!-- AI 输入面板（idle 首次进入 / completed 支持再次搜索） -->
    <div v-if="diagnosisStatus === 'idle' || diagnosisStatus === 'completed'" class="diagnosis-panel">
      <div class="panel-header">
        <span class="pdot cyan"></span>{{ diagnosisStatus === 'completed' ? '再次诊断' : 'AI智能诊断输入' }}
        <span class="pid">品牌渠道自动调研</span>
      </div>
      <div class="panel-body">
        <div class="ai-hero">
          <h2 v-if="diagnosisStatus === 'completed'">换一个品牌，<span class="glow">继续AI诊断</span></h2>
          <h2 v-else>输入品牌名，<span class="glow">AI自动完成诊断</span></h2>
          <p>系统将自动搜索品牌在天猫、京东、抖音、小红书等平台的渠道覆盖情况，分析DTC布局和分销体系，生成18项指标评分与优化建议。</p>
          <!-- 接口错误提示 -->
          <div v-if="errorMsg" class="ai-error">
            <span class="ae-icon">✕</span>
            <span>{{ errorMsg }}</span>
          </div>
          <div class="ai-form">
            <div class="ai-field">
              <label>品牌名称</label>
              <input
                type="text"
                class="ai-input"
                v-model="brandName"
                placeholder="例如：蒙牛、海尔、花西子..."
                @keyup.enter="startDiagnosis"
                @blur="onBrandBlur"
              />
            </div>
            <div class="ai-field">
              <label>行业 / 品类 <span class="ai-industry-hint">输入品牌名称后自动选择行业</span> <span v-if="detectingIndustry" class="ai-industry-loading">AI识别中...</span><span v-else-if="aiDetectedIndustry" class="ai-industry-tag">AI已匹配</span></label>
              <select class="ai-select" v-model="industry">
                <option value="">请选择行业/品类</option>
                <option v-for="ind in industries" :key="ind" :value="ind">{{ ind }}</option>
              </select>
            </div>
            <button
              class="ai-btn"
              :disabled="!brandName.trim() || !industry"
              @click="startDiagnosis"
            >
              {{ diagnosisStatus === 'completed' ? '再次诊断' : '开始AI诊断' }}
            </button>
            <div class="ai-hint">基于真实网络搜索数据 · 约15-30秒完成</div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI 加载动画 -->
    <div v-if="diagnosisStatus === 'loading'" class="diagnosis-panel">
      <div class="panel-header">
        <span class="pdot cyan"></span>AI调研中
        <span class="pid">正在分析渠道数据</span>
      </div>
      <div class="panel-body">
        <div class="ai-loading">
          <div class="radar">
            <div class="radar-sweep"></div>
            <div class="radar-dot"></div>
          </div>
          <div class="load-title">{{ currentStepTitle }}</div>
          <div class="load-steps">
            <div
              v-for="(step, idx) in loadingSteps"
              :key="idx"
              class="load-step"
              :class="{ active: currentStep === idx, done: currentStep > idx }"
            >
              <span class="ls-icon">{{ currentStep > idx ? '✓' : '○' }}</span>
              <span class="ls-text">{{ step.label }}</span>
              <span class="ls-bar"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { INDUSTRIES, DEFAULT_SCORES } from '../../utils/constants'
import diagnosisApi from '../../api/diagnosis'

export default {
  name: 'AiDiagnosisTab',
  data() {
    return {
      brandName: '',
      industry: '',
      errorMsg: '',
      detectingIndustry: false,
      aiDetectedIndustry: false,
      currentStep: -1,
      loadingSteps: [
        { label: '搜索主流电商平台覆盖' },
        { label: '检测内容/社交电商布局' },
        { label: '分析DTC自有渠道' },
        { label: '评估分销体系' },
        { label: '分析业态多样性' },
        { label: '生成诊断结果' }
      ]
    }
  },
  computed: {
    ...mapState('diagnosis', ['diagnosisStatus', 'aiBrand', 'aiIndustry', 'scores']),
    industries() { return INDUSTRIES },
    currentStepTitle() {
      const titles = [
        `正在搜索 ${this.brandName} 在 BRM 系统中的渠道数据...`,
        `正在搜索 ${this.brandName} 在天猫/京东/拼多多的覆盖...`,
        `正在检测 ${this.brandName} 在抖音/小红书/快手的布局...`,
        `正在分析 ${this.brandName} 的业态多样性和DTC渠道...`,
        `正在评估 ${this.brandName} 的分销体系和渠道健康度...`,
        `正在生成 ${this.brandName} 的诊断结果...`
      ]
      return this.currentStep >= 0 && this.currentStep < titles.length
        ? titles[this.currentStep]
        : '正在初始化诊断引擎...'
    }
  },
  mounted() {
    // 登录类型
    const logintype = localStorage.getItem('token')
    const isKefu = logintype === 'KEFU'
    let brand = ''
    if (!isKefu) {
      // 品牌名来源优先级：URL query.brand（登录跳转带来）> localStorage.diagnosisBrand > store.aiBrand
      const qBrand = this.$route.query && this.$route.query.brand
      const lsBrand = localStorage.getItem('diagnosisBrand')
      brand = qBrand || lsBrand || this.aiBrand
      // 登录带入的品牌名无 URL 参数时，补到地址栏（/diagnosis?brand=xxx）
      if (!qBrand && lsBrand) {
        this.$router.replace({ path: '/diagnosis', query: { brand: lsBrand } })
      }
    }
    if (brand) {
      this.brandName = brand
      if (this.aiIndustry) this.industry = this.aiIndustry
      // HCM 账户：自动填入品牌名后，自动识别行业/品类
      if (logintype === 'HCM' && !this.industry) {
        this._detectIndustry(brand)
      }
    }
  },
  methods: {
    ...mapMutations('diagnosis', [
      'SET_AI_MODE', 'SET_AI_BRAND', 'SET_AI_INDUSTRY',
      'SET_DIAGNOSIS_STATUS', 'SET_BATCH_SCORES', 'SET_BRM_DATA', 'SET_AI_DATA'
    ]),
    ...mapActions('diagnosis', ['setScore', 'resetDiagnosisData']),

    /** 品牌名失焦时自动识别行业 */
    async onBrandBlur() {
      const brand = this.brandName.trim()
      if (!brand || brand.length < 2) return
      await this._detectIndustry(brand)
    },

    /** 识别品牌所属行业/品类（品牌名失焦 / HCM 自动填入后调用） */
    async _detectIndustry(brand) {
      this.detectingIndustry = true
      this.aiDetectedIndustry = false
      try {
        const res = await diagnosisApi.detectIndustry(brand)
        // 兼容两种返回结构：
        //   { data: { industry } }         → res.data.industry
        //   { data: { data: { industry }}} → res.data.data.industry
        const ind = res && res.data && (res.data.industry || (res.data.data && res.data.data.industry))
        if (ind) {
          // 归一化匹配：清洗接口值中的不可见字符/空白后，与本地选项精确匹配
          // 命中则回填本地选项值，保证下拉框一定能选中
          const matched = this._matchIndustry(ind)
          if (matched) {
            this.industry = matched
            // 识别为"其他"表示 AI 无法确定具体行业，不标记"AI已匹配"
            this.aiDetectedIndustry = matched !== '其他'
          }
        }
      } catch (e) {
        // 接口不可用时静默失败，用户手动选择
      }
      this.detectingIndustry = false
    },

    /**
     * 归一化后匹配本地行业选项，返回本地选项值；未命中返回 null
     * 清洗范围：零宽字符(\u200B-\u200D)、BOM(\uFEFF)、NBSP(\u00A0)、全角空格(\u3000)、常规空白
     */
    _matchIndustry(val) {
      const norm = s => String(s || '').replace(/[\u200B-\u200D\uFEFF\u00A0\u3000\s]/g, '').trim()
      const target = norm(val)
      if (!target) return null
      return this.industries.find(i => norm(i) === target) || null
    },

    async startDiagnosis() {
      const brand = this.brandName.trim()
      // 品牌名称 + 行业/品类 缺一不可
      if (!brand || !this.industry) {
        this.$message.warning('请填写品牌名称并选择行业/品类后再开始诊断')
        return
      }

      // 先清空上次诊断的结果数据（评分/报告/建议回到默认状态）
      this.resetDiagnosisData()

      this.SET_AI_MODE(true)
      this.SET_AI_BRAND(brand)
      this.SET_AI_INDUSTRY(this.industry)
      this.SET_DIAGNOSIS_STATUS('loading')
      this.errorMsg = ''

      // 模拟AI搜索步骤
      this.currentStep = -1
      for (let i = 0; i < 6; i++) {
        this.currentStep = i
        await this._delay(500 + Math.random() * 500)
      }

      // ─── 调真实接口（BRM数据 + AI搜索）───
      // 任一接口报错 → 显示错误信息并终止，不再往下执行
      const [brmRes, aiRes] = await Promise.all([
        diagnosisApi.getBrmData(brand).catch(err => ({ __error: err })),
        diagnosisApi.aiSearch(brand, this.industry).catch(err => ({ __error: err }))
      ])

      const errMsg = this._getApiError(brmRes) || this._getApiError(aiRes)
      if (errMsg) {
        this.SET_AI_MODE(false)
        this.SET_DIAGNOSIS_STATUS('idle')
        this.errorMsg = errMsg
        this.$message.error(errMsg)
        return
      }

      const brmData = brmRes && brmRes.data !== undefined ? brmRes.data : null
      const aiData = aiRes && aiRes.data !== undefined ? aiRes.data : null

      // 字段兼容：后端返回 aiFindings，前端统一读取 findings
      if (aiData && aiData.aiFindings && !aiData.findings) {
        aiData.findings = aiData.aiFindings
      }

      // ─── 合并评分 ───
      const scores = {}
      const brmSource = {}

      if (brmData && brmData.brmScores) {
        for (const qid in brmData.brmScores) {
          scores[qid] = brmData.brmScores[qid].score
          brmSource[qid] = true
        }
      }

      if (aiData && aiData.scores) {
        for (const qid in aiData.scores) {
          scores[qid] = aiData.scores[qid]
          if (brmSource[qid]) delete brmSource[qid]
        }
      } else if (!brmData) {
        // 既无BRM也无AI数据 → 默认中等分
        for (const qid in DEFAULT_SCORES) {
          scores[qid] = DEFAULT_SCORES[qid]
        }
      }

      this.SET_BATCH_SCORES({ scores, brmSource })
      this.SET_BRM_DATA(brmData)
      this.SET_AI_DATA(aiData)
      this.SET_DIAGNOSIS_STATUS('completed')

      // 通知父组件
      this.$emit('diagnosis-complete')
    },

    /**
     * 提取接口错误信息：HTTP/网络错误或业务失败（status 非成功值）
     * 返回空串表示接口正常
     */
    _getApiError(res) {
      if (!res) return '接口无响应'
      if (res.__error) {
        const err = res.__error
        const ctx = err.__ctx ? `${err.__ctx}：` : ''
        // 后端返回了错误体时优先展示其中的 message
        if (err.response && err.response.data) {
          try {
            const d = typeof err.response.data === 'string' ? JSON.parse(err.response.data) : err.response.data
            if (d && d.message) return `${ctx}${d.message}（HTTP ${err.response.status}）`
          } catch (e) { /* 忽略解析失败 */ }
        }
        // 有 HTTP 状态码
        if (err.response) return `${ctx}请求失败（HTTP ${err.response.status}）`
        // 纯网络错误
        return `${ctx}${err.message || '网络请求失败'}`
      }
      // ResultEntity 业务状态判断：status 成功值为 10000 / 200
      const s = res.status
      if (s !== undefined && String(s) !== '10000' && String(s) !== '200') {
        return res.message || `接口业务失败（status=${s}）`
      }
      return ''
    },

    _delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    }
  }
}
</script>
